import { lighten } from './lighten';
import meetsContrastGuidelines from './meetsContrastGuidelines';

/**
 * Ensures a color meets WCAG contrast guidelines with black text.
 * If it doesn't meet the guidelines, it progressively lightens the color
 * until it meets the required contrast ratio.
 *
 * @param color The base color to check and possibly lighten
 * @param textColor The text color to check contrast against (defaults to black)
 * @param wcagLevel The WCAG level to check against ('AA' or 'AAA')
 * @param lightenStep The amount to lighten by in each iteration (0-1)
 * @param maxIterations Maximum number of iterations to prevent infinite loops
 * @returns A color that meets the contrast guidelines
 */
export function ensureAccessibleContrastLightening(
  color: string,
  textColor: string = '#000000',
  wcagLevel: 'AA' | 'AAA' = 'AA',
  lightenStep: number = 0.1,
  maxIterations: number = 50,
): string {
  // Return the color if it's already meeting guidelines
  let currentColor = color;
  let iterations = 0;

  while (iterations < maxIterations) {
    const guidelines = meetsContrastGuidelines(currentColor, textColor);

    // Check if the color meets the specified WCAG level
    if (
      (wcagLevel === 'AA' && guidelines.AA) ||
      (wcagLevel === 'AAA' && guidelines.AAA)
    ) {
      return currentColor;
    }

    // Lighten the color and try again
    currentColor = lighten(lightenStep, currentColor);
    iterations++;
  }

  // Return the lightened color after max iterations
  return currentColor;
}

export default ensureAccessibleContrastLightening;
